import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { StudentInfo } from '../../types';
import toast from 'react-hot-toast';
import glassTheme, { glassMixins } from '../../styles/glassTheme';

const FormContainer = styled.div`
  ${glassTheme.glass.heavy}
  padding: 3rem;
  width: 90%;
  max-width: 600px;
  box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 0.5rem;
  font-size: 2rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 2rem;
  font-size: 1rem;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 0.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${glassTheme.colors.text.primary};
  font-weight: 500;
  font-size: 0.95rem;

  &::before {
    content: '* ';
    color: ${glassTheme.colors.status.error};
    font-weight: bold;
  }
`;

const Input = styled.input`
  ${glassMixins.input}
  width: 100%;
  padding: 1rem;
`;

const Select = styled.select`
  ${glassMixins.input}
  width: 100%;
  padding: 1rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 3rem;
`;

const Button = styled.button`
  ${glassMixins.button}
  width: 100%;
  padding: 1rem 2rem;
  margin-top: 1rem;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  color: ${glassTheme.colors.status.error};
  font-size: 0.85rem;
  margin-top: 0.5rem;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const GlobalErrorMessage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid ${glassTheme.colors.status.error};
  color: ${glassTheme.colors.status.error};
  padding: 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  margin-bottom: 1.5rem;
  text-align: center;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const STATS_LEVELS = [
  { value: 'none', label: '完全小白' },
  { value: 'beginner', label: '一点点' },
  { value: 'basic', label: '中等水平' },
  { value: 'intermediate', label: '中高等水平' },
  { value: 'advanced', label: '专业' }
] as const;

interface UserInfoFormProps {
  onComplete?: () => void;
}

export const UserInfoForm: React.FC<UserInfoFormProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const { logout, updateStudentInfo, setInfoCompleted, isLoading, error, clearError, user, refreshUser } = useAuthStore();
  const [formData, setFormData] = useState<StudentInfo>({
    studentId: '',
    realName: '',
    gender: '',
    birthDate: '',
    grade: '',
    college: '',
    major: '',
    hasStatsBackground: null,
    statsLevel: 'none'
  });
  const [errors, setErrors] = useState<Partial<Record<keyof StudentInfo, string>>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof StudentInfo, string>> = {};

    if (!formData.studentId.trim()) newErrors.studentId = '请输入学号';
    if (!formData.realName.trim()) newErrors.realName = '请输入姓名';
    if (!formData.gender) newErrors.gender = '请选择性别';
    if (!formData.birthDate) newErrors.birthDate = '请选择出生年月';
    if (!formData.grade.trim()) newErrors.grade = '请输入年级';
    if (!formData.college.trim()) newErrors.college = '请输入学院';
    if (!formData.major.trim()) newErrors.major = '请输入专业';
    if (formData.hasStatsBackground === null) newErrors.hasStatsBackground = '请选择是否学过统计课程';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    clearError();

    try {
      // 调用 API 保存用户信息
      const success = await updateStudentInfo(formData);

      if (success) {
        setInfoCompleted(true);
        toast.success('个人信息保存成功');


        // Refresh user data from server to get latest enrolledCourses
        await refreshUser();

        if (onComplete) {
          onComplete();
        } else {
          // Redirect based on user role
          const primaryRole = user?.primaryRole || user?.roles?.[0]?.name || 'student';
          switch (primaryRole) {
            case 'admin':
            case 'super_admin':
              navigate('/admin/dashboard');
              break;
            case 'content_manager':
              navigate('/content-manager/dashboard');
              break;
            default:
              navigate('/learn');
              break;
          }
        }
      } else {
        // 如果保存失败，显示错误信息
        toast.error(error || '保存失败，请重试');
      }
    } catch (err) {
      console.error('保存用户信息失败:', err);
      toast.error('保存失败，请重试');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleChange = (field: keyof StudentInfo, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <FormContainer>
      <FormTitle>完善个人信息</FormTitle>
      <FormSubtitle>请填写以下信息以完成注册（所有字段均为必填）</FormSubtitle>

      <GlobalErrorMessage visible={!!error}>
        {error}
      </GlobalErrorMessage>

      <form onSubmit={handleSubmit}>
        <FormGrid>
          <FormGroup>
            <Label>学号</Label>
            <Input
              type="text"
              value={formData.studentId}
              onChange={(e) => handleChange('studentId', e.target.value)}
              placeholder="请输入学号"
            />
            <ErrorMessage visible={!!errors.studentId}>{errors.studentId}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>姓名</Label>
            <Input
              type="text"
              value={formData.realName}
              onChange={(e) => handleChange('realName', e.target.value)}
              placeholder="请输入真实姓名"
            />
            <ErrorMessage visible={!!errors.realName}>{errors.realName}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>性别</Label>
            <Select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value as 'male' | 'female' | '')}
            >
              <option value="">请选择</option>
              <option value="male">男</option>
              <option value="female">女</option>
            </Select>
            <ErrorMessage visible={!!errors.gender}>{errors.gender}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>出生年月</Label>
            <Input
              type="month"
              value={formData.birthDate}
              onChange={(e) => handleChange('birthDate', e.target.value)}
              max={new Date().toISOString().slice(0, 7)}
            />
            <ErrorMessage visible={!!errors.birthDate}>{errors.birthDate}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>年级</Label>
            <Select
              value={formData.grade}
              onChange={(e) => handleChange('grade', e.target.value)}
            >
              <option value="">请选择年级</option>
              <option value="大一">大一</option>
              <option value="大二">大二</option>
              <option value="大三">大三</option>
              <option value="大四">大四</option>
              <option value="研一">研一</option>
              <option value="研二">研二</option>
              <option value="研三">研三</option>
              <option value="博士">博士</option>
            </Select>
            <ErrorMessage visible={!!errors.grade}>{errors.grade}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>学院</Label>
            <Input
              type="text"
              value={formData.college}
              onChange={(e) => handleChange('college', e.target.value)}
              placeholder="请输入学院名称"
            />
            <ErrorMessage visible={!!errors.college}>{errors.college}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>专业</Label>
            <Input
              type="text"
              value={formData.major}
              onChange={(e) => handleChange('major', e.target.value)}
              placeholder="请输入专业名称"
            />
            <ErrorMessage visible={!!errors.major}>{errors.major}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>是否学过统计课程</Label>
            <Select
              value={formData.hasStatsBackground === null ? '' : formData.hasStatsBackground.toString()}
              onChange={(e) => handleChange('hasStatsBackground', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)}
            >
              <option value="">请选择</option>
              <option value="true">是</option>
              <option value="false">否</option>
            </Select>
            <ErrorMessage visible={!!errors.hasStatsBackground}>{errors.hasStatsBackground}</ErrorMessage>
          </FormGroup>

          <FormGroup>
            <Label>统计学知识水平</Label>
            <Select
              value={formData.statsLevel}
              onChange={(e) => handleChange('statsLevel', e.target.value as StudentInfo['statsLevel'])}
            >
              {STATS_LEVELS.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>

        <Button type="submit" disabled={isLoading}>
          {isLoading ? '提交中...' : '完成注册'}
        </Button>

        <Button
          type="button"
          onClick={handleLogout}
          style={{ marginTop: '1rem', background: 'transparent', border: `2px solid ${glassTheme.colors.border}` }}
        >
          取消并退出
        </Button>
      </form>
    </FormContainer>
  );
};
